import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import BackToTop from "../../components/backToTop/BackToTop";
import FAQSection from "../../components/FAQSection/FAQSection";
import api from "../../utils/api";
import { toast } from "react-hot-toast";
import "./GetHelpPage.css";

const GetHelpPage = () => {
  const [searchParams] = useSearchParams();
  // Arriving from a provider's "Contact via TutorsLinks" button — providers'
  // phone numbers aren't public, so this is how a prospective student/parent
  // reaches TutorsLinks to be connected with them.
  const contactProviderName = searchParams.get("name");
  const contactProviderId = searchParams.get("provider");
  // "enrolment" arrives from a training centre's "Request Information /
  // Enrolment Enquiry" button — same contact-routing flow, different
  // prefilled intent so the ticket reads correctly on the admin side.
  const contactIntent = searchParams.get("intent");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    description: contactProviderName
      ? contactIntent === "enrolment"
        ? `Hi, I'd like to enquire about enrolment at ${contactProviderName} (provider ID: ${contactProviderId || "n/a"}) on TutorsLinks.`
        : `Hi, I'd like to be connected with ${contactProviderName} (provider ID: ${contactProviderId || "n/a"}) on TutorsLinks.`
      : "",
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+234");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const countryCodes = [
    { code: "+234", name: "Nigeria" },
    { code: "+1", name: "United States" },
    { code: "+44", name: "United Kingdom" },
    { code: "+233", name: "Ghana" },
    { code: "+27", name: "South Africa" },
    { code: "+254", name: "Kenya" },
    { code: "+256", name: "Uganda" },
    { code: "+263", name: "Zimbabwe" },
    { code: "+265", name: "Malawi" },
  ];

  useEffect(() => {
    // Scroll to top smoothly when the component mounts / route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth" if you prefer a gentle scroll
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (contactProviderId) {
        // Arrived via a provider's "Contact"/"Request Information" button —
        // this is a trackable Enquiry (provider gets notified directly, admin
        // can measure response rate/conversion), not a general support ticket.
        await api.post("/enquiries", {
          providerId: contactProviderId,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          phone: formData.phone
            ? `${selectedCountryCode}${formData.phone}`
            : "",
          message: formData.description,
          intent: contactIntent === "enrolment" ? "enrolment" : "general",
        });
        toast.success("Enquiry sent — the provider has been notified!");
      } else {
        await api.post("/contact", {
          ...formData,
          phoneCountryCode: selectedCountryCode,
        });
        toast.success("Message sent! Check your inbox for a confirmation.");
      }

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        description: "",
      });
    } catch (err) {
      console.error("Contact form error:", err);
      toast.error(
        "Something went wrong. Please try again or email us directly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header
        isMenuOpen={isMenuOpen}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
      />

      <section className="contact-hero">
        <div className="contact-container">
          <div className="contact-content">
            <div className="contact-header">
              <h1 className="contact-heading">Contact Our Team</h1>
              <p className="contact-subtitle">
                Have a question or need assistance? We'd love to hear from you.
                Whether you have a question, comment, or require support, we're
                here to help.
              </p>
            </div>

            <div className="contact-main-grid">
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="get-help-label" htmlFor="firstName">
                      First Name
                    </label>
                    <input
                      className="get-help-input"
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="get-help-label" htmlFor="lastName">
                      Last Name
                    </label>
                    <input
                      className="get-help-input"
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="get-help-label" htmlFor="email">
                    Email
                  </label>
                  <input
                    className="get-help-input"
                    type="email"
                    id="email"
                    name="email"
                    placeholder="tutorslinks@tutorslinks.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="get-help-label" htmlFor="phone">
                    Phone Number
                  </label>
                  <div className="phone-input-wrapper">
                    <select
                      value={selectedCountryCode}
                      onChange={(e) => setSelectedCountryCode(e.target.value)}
                      className="country-code-select"
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      className="get-help-input"
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="80 234 567 89"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="get-help-label" htmlFor="description">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    className="get-help-description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="6"
                    placeholder="Please describe your question or issue"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="help-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message <span className="arrow">→</span>
                    </>
                  )}
                </button>
              </form>

              <div className="contact-sidebar">
                <div className="chat-with-us">
                  <h3>Chat With Us</h3>
                  <p>Speak to our friendly team via live chat</p>
                  <div className="contact-option-row">
                    <img
                      src="/images/send-message.png"
                      alt="Live Chat"
                      className="contact-icon"
                    />
                    <div className="option-content">
                      {/* <p className="option-label">Start a live chat</p> */}
                      <a
                        href="https://wa.me/234810449810"
                        className="chat-link"
                      >
                        Start a live chat
                      </a>
                    </div>
                  </div>

                  <div className="contact-option-row">
                    <img
                      src="/images/send-email.png"
                      alt="Email"
                      className="contact-icon"
                    />
                    <div className="option-content">
                      {/* <p className="option-label">Send us an email</p> */}
                      <a
                        href="mailto:support@tutorslinks.com"
                        className="email-link"
                      >
                        Send us an email
                      </a>
                    </div>
                  </div>
                  <div className="contact-option-row">
                    <div className="option-content">
                      <h3 className="call-us-title">Call Us</h3>
                      <p>Call our team Mon-Fri from 8am - 5pm</p>
                      <div className="contact-option-row">
                        <img
                          src="/images/phone-call.png"
                          alt="phone"
                          className="contact-icon"
                        />
                        <div className="option-content">
                          <p className="phone-number"> +2348064738240</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="contact-option-row office-address">
                    <div className="option-content">
                      <h3 className="office-title">
                        TutorsLinks by Efiko Edutech LTD.
                      </h3>
                      <p className="office-address-text">
                        68 Emeka Anyaoku Street, Area 11, Garki, Abuja
                      </p>
                      <iframe
                        src="https://maps.google.com/maps?q=9.040748,7.493697&z=15&output=embed"
                        width="100%"
                        height="200"
                        style={{ borderRadius: "10px", marginTop: "10px" }}
                        loading="lazy"
                      ></iframe>
                      <a
                        href="https://maps.google.com/?q=9.040748,7.493697"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="map-link"
                      >
                        View on Google Maps
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <FAQSection />
      <Footer />
      <BackToTop />
    </>
  );
};

export default GetHelpPage;
